import { html, nothing } from 'lit';

import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { hasContent } from '@common/lit/hasContent';
import { Generation } from '@api-consent/types/common';
import { type TefComsConsentGroup } from '@src/components/tef-coms-consent-group';
import { filterPredicator } from '@src/components/templates/util';
import { store } from '@src/store';
import { cancelMicrofrontend } from '@src/store/consents';
import { ConsentGroup, ConsentType } from '@src/types/common';

export const clickTextTemplate = (
  checkBoxId: string,
  comsConsentGroup: TefComsConsentGroup,
) => {
  const { oneclick: isOneClick, name } = comsConsentGroup.consentGroup as ConsentGroup;

  let labelText = comsConsentGroup.consentGroup!.text;
  if (isOneClick && hasContent(comsConsentGroup.consentGroup!.oneclickScope)) {
    labelText = comsConsentGroup.consentGroup!.oneclickScope as string;
  }

  return html`
    <div class="form-group-checkbox">
      <input id=${checkBoxId} type="checkbox" @click=${comsConsentGroup.oneclickAction.bind(comsConsentGroup)} />
      <label for=${checkBoxId}  data-testid="oneclick-checkbox-label-${name.toLowerCase()}">
        ${unsafeHTML(labelText)}
      </label>
    </div>
  `;
};

export const clickConsentsContainerTemplate = (
  stylecontext: string,
  comsConsentGroup: TefComsConsentGroup,
  collapsibleOpenGroupName: string,
) => {
  const { oneclickCollapsible } = comsConsentGroup.consentGroup as ConsentGroup;
  const STANDARD_ONCLICK_TEXT = 'Wenn ich einzelne Kontaktkanäle und Verarbeitungsvorgänge von meiner Auswahl ausschließen möchte, kann ich hier gesonderte Einwilligungen erteilen.';

  return html`
    <tef-coms-collapsible
        once
        cloak
        context="${stylecontext}"
        ?is-open="${comsConsentGroup.groupName === collapsibleOpenGroupName}"
      >
        <div slot="title">
          ${oneclickCollapsible ? unsafeHTML(oneclickCollapsible) : STANDARD_ONCLICK_TEXT}
        </div>
        <div slot="content">
          <!-- consentPrefix -->
          ${comsConsentGroup.consentPrefix ? html`<p>${unsafeHTML(comsConsentGroup.consentPrefix)}</p>` : nothing}
          ${comsConsentGroup.groupConsents('group')}
        </div>
      </tef-coms-collapsible>
    `;
};

export const groupConsentsTemplate = (
  comsConsentGroup: TefComsConsentGroup,
) => {
  const { consents, required, displayMode } = comsConsentGroup.consentGroup as ConsentGroup;
  return html`
    <ul class="list">
      ${consents /* eslint-disable @typescript-eslint/indent */
        .filter(filterPredicator)
        .map(
          (consent) => html`
            ${consent.hintBefore ? html`<li class="coms-hint-before">${unsafeHTML(consent.hintBefore)}</li>` : nothing}
            <li class="coms-consent">
              <tef-coms-consent .consent=${consent} ?required=${required} .displayMode=${displayMode}></tef-coms-consent>
            </li>
            ${consent.hintAfter
              ? html`<li class="coms-hint-after">${unsafeHTML(consent.hintAfter)}</li>`
              : nothing
            }`,
        )/* eslint-enable @typescript-eslint/indent */}
    </ul>
  `;
};

export const withdrawalTemplate = (
  comsConsentGroup: TefComsConsentGroup,
  checkboxId: string,
) => {
  const { denyall } = comsConsentGroup.consentGroup as ConsentGroup;
  return html`
    <div class="form-row">
      <div class="form-group-checkbox">
        <input
          id=${checkboxId}
          type="checkbox"
          .checked=${comsConsentGroup.noConsentSet}
          @click=${comsConsentGroup.denyAction.bind(comsConsentGroup)}
        />
        <label for=${checkboxId}>
          ${unsafeHTML(denyall!.toString())}
        </label>
      </div>
    </div>
    `;
};

export const groupViewTemplate = (
  comsConsentGroup: TefComsConsentGroup,
  consentGroupsView: ConsentGroup[],
  filteredConsents: ConsentType[],
  stylecontext: string,
) => {
  const {
    header, text, footer, denyallView, displayMode,
  } = consentGroupsView[comsConsentGroup.groupIndex];

  // Do we need a "Zurück" button?
  let backButtonHtml = html`${nothing}`;
  if (comsConsentGroup.mode.includes('buttons:cancel')) {
    backButtonHtml = html`
        <button @click=${() => { store.dispatch(cancelMicrofrontend()); }} class="button button-border" data-testid="button-back">Zurück</button>
      `;
  }

  return html`
    <h4 class="typography-display--4">
      ${unsafeHTML(header)}
    </h4>
    <p>
      ${unsafeHTML(text)}
    </p>
    ${(filteredConsents.length === 0 && denyallView) ? html`
      <div class="coms-ocw">${unsafeHTML(denyallView)}</div>
      ` : html`
      <tef-coms-collapsible-group context="${stylecontext}">
        ${filteredConsents.map((consent) => html`
          <tef-coms-collapsible context="${stylecontext}">
            <div slot="title">
              ${unsafeHTML(consent.shortInfo)}
            </div>
            <div slot="content">
              <!-- consentPrefix -->
              ${comsConsentGroup.consentPrefix ? unsafeHTML(comsConsentGroup.consentPrefix) : nothing}
              <tef-coms-consent .mode=${comsConsentGroup.mode} .consent=${consent} .displayMode=${displayMode}></tef-coms-consent>
            </div>
          </tef-coms-collapsible>
        `)}
      </tef-coms-collapsible-group>
    `}
    ${footer ? html`
      <p>
        ${unsafeHTML(footer)}
      </p>
    ` : nothing}

    <div class="form-row form-row--buttons section-buttons">
      ${backButtonHtml}
      <button @click=${comsConsentGroup.openGroupForEdit.bind(comsConsentGroup)} class="button button-default" data-testid="button-update">Ändern</button>
    </div>
  `;
};

export const groupTemplate = (comsConsentGroup: TefComsConsentGroup) => {
  const { header, footer, oneclick: isOneClick } = comsConsentGroup.consentGroup as ConsentGroup;
  const isAtLeastGenerationUnlock = comsConsentGroup.generation >= Generation.UNLOCK;

  return html`
    <h4 class="typography-display--4">${unsafeHTML(header)}</h4>
    ${isOneClick && isAtLeastGenerationUnlock ? comsConsentGroup.groupText : nothing}
    <p>
      ${isOneClick ? comsConsentGroup.groupOneclickText : comsConsentGroup.groupText}
    </p>
    <!-- consentPrefix -->
    ${!isOneClick && comsConsentGroup.consentPrefix ? html`<p>${unsafeHTML(comsConsentGroup.consentPrefix)}</p>` : nothing}
    <div>
      ${isOneClick ? comsConsentGroup.groupOneclickConsentsContainer : comsConsentGroup.groupConsents()}
    </div>
    ${footer ? html`<p>${unsafeHTML(footer)}</p>` : nothing}
    ${comsConsentGroup.withdrawal}`;
};
